import { Badge, Box, Card, CardBody, CardHeader, Flex, Grid, GridItem, Text, useTheme, VStack } from "@chakra-ui/react";
import { getBadgeColor } from "../helper";

import React, { useEffect, useState } from "react";
import { routes } from "config/paths";
import A from "components/A";
import MarkdownText from "components/MarkdownText";
import { CaretCircleDown, CaretCircleUp, ListBullets, UserGear, UsersFour } from "@phosphor-icons/react";
import PeopleTag from "../../components/PeopleTag/PeopleTag";
import { useSearchParams } from "react-router-dom";
import { RichTextViewer } from "./RichTextEditor/RichTextEditor";
import { isMultipartQuestion, multipartAnswerToString } from "../helper";
import { modifiedUrl } from "utils";
import { isExcludedFn } from "api/issb/Question";
import AdditionalStatuses from "./AdditionalStatuses";
import { AButton } from "components/A";

// Performance notes - from React Profiler > Timeline, on Daniel's laptop, Feb 17 2025
// Header on/off: 800ms
// MarkdownText: 300ms
// Body on/off: 2500ms <-- Note that the Collapse animation means this is rendered even though hidden
// Rest of card: 400ms
// Rest of page: 200ms (because it doesn't re-render)
// Conclusion: Removing the Collapse animation from the body cuts 2/3 of the time.

/**
 * A collapsible card for a question -- shows the answer when opened.
 * @param {Question} question - The question object
 */
const QuestionCard = ({
  question,
  withDetails = true,
  showExcluded = false,
  showMultiPartAnswer = false,
  expandCard = false,
  hasUnsavedChanges = false,
}) => {
  const q = question;
  const [isOpen, setIsOpen] = useState(false);
  const toggleDetails = () => {
    setIsOpen(!isOpen);
  };

  // If the expanded all is set to true then automatically set the card to open up.
  useEffect(() => {
    if (expandCard === true) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [expandCard]);

  const { colors } = useTheme();

  // mark dependent (status unknown) and excluded questions
  if (isExcludedFn(question) && !showExcluded) {
    console.log("(odd - should be filtered out) Excluded", question);
    return null;
  }
  let opacity = 1;
  // grey out undetermined questions (HACK prefer explicit relevance, but allow for not-calculated -- assuming that means undetermined)
  if (question?.relevance === "UNDETERMINED" || (!question?.relevance && question?.condition)) {
    opacity = 0.6;
  }
  if (isExcludedFn(question)) {
    opacity = 0.3;
  }

  return (
    <Card
      opacity={opacity}
      w="100%"
      color="teal"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      boxShadow="sm"
      bg="gray.50"
    >
      <CardHeader
        _hover={{
          bg: "gray.100",
        }}
      >
        <Grid templateColumns="1fr 50px">
          <GridItem>
            <QCardHeader
              assignees={q?.assignees}
              reference={q?.issbReference}
              title={q?.question}
              stage={q?.stage}
              stageType={q?.stageType}
              owner={q?.owner}
              id={q?.id}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          </GridItem>

          <GridItem justifySelf="end" alignSelf="end">
            <Box onClick={toggleDetails} cursor="pointer">
              {isOpen ? (
                <CaretCircleUp color={colors.gray[500]} weight="fill" size={28} />
              ) : (
                <CaretCircleDown color={colors.gray[500]} weight="fill" size={28} />
              )}
            </Box>
          </GridItem>
        </Grid>
      </CardHeader>

      {isOpen && <QCardBody q={q} showMultiPartAnswer={showMultiPartAnswer} />}
    </Card>
  );
};

/**
 * The main part of the card
 */
export function QCardHeader({ reference, title, stage, stageType, owner, assignees, id, hasUnsavedChanges = false}) {
  const [searchParams] = useSearchParams(); // pass along q so we can come back to this list
  const qParam = searchParams.get("q");
  const href = modifiedUrl({ id, q: qParam }, routes.issbAnswerDetail);

  const confirmProps = hasUnsavedChanges
    ? {
      shouldConfirm: true,
      confirmConfig: {
        title: "Unsaved changes?",
        message:
          "It looks like you are navigating away with unsaved changes. Continuing will mean these changes will be lost.",
        confirmText: "Discard Changes",
        cancelText: "Cancel",
      },
    }
    : {};

  return (
    <A
      href={href}
      variant="link"
      style={{ textAlign: "left", width: "100%" }}
      {...confirmProps}
    >
      <VStack align="start" spacing={2}>
        <Text fontSize="body.xs" color="gray.500" fontWeight="normal">
          {reference}
        </Text>
        <Flex justify="space-between" align="center" w="100%">
          <MarkdownText color="teal" fontSize="heading.s" fontWeight="bold">
            {title}
          </MarkdownText>
        </Flex>
      </VStack>
    </A>
  );
}

const QCardItems = ({ stage, stageType, owner, assignees, id, readOnly }) => {
  const { colors } = useTheme();

  return (
    <Flex direction="column" gap={4} mt={0} mb={4}>
      {/* Owner */}
      <Grid templateColumns="100px 1fr" gap={2} alignItems="center">
        <GridItem>
          <Flex alignItems="center" gap={2}>
            <UserGear color={colors.gray[400]} size={18} />
            <Text variant="statusTitle">Owner</Text>
          </Flex>
        </GridItem>
        <GridItem>
          <PeopleTag type="owner" users={[owner]} questionIds={[id]} />
        </GridItem>
      </Grid>

      {/* Assignees */}
      <Grid templateColumns="100px 1fr" gap={2} alignItems="center">
        <GridItem>
          <Flex alignItems="center" gap={2}>
            <UsersFour size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Assignees</Text>
          </Flex>
        </GridItem>
        <GridItem>
          <PeopleTag type="assignee" users={assignees} questionIds={[id]} alignment="row" readOnly={readOnly} />
        </GridItem>
      </Grid>

      {/* Stages */}
      <Grid templateColumns="100px 1fr" gap={2} alignItems="center">
        <GridItem>
          <Flex alignItems="center" gap={2}>
            <ListBullets size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Stage</Text>
          </Flex>
        </GridItem>
        <GridItem>
          <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme={getBadgeColor(stageType)}>
            {stage}
          </Badge>
        </GridItem>
      </Grid>
    </Flex>
  );
};

/**
 * Answer and some other details. Shown if the user clicks "View Answer"
 *
 * Only the details for multipart questions as the answer is often ungainly to try and display in a small space.
 * (ESG-3287)
 */
export function QCardBody({ q, showMultiPartAnswer, readOnly }) {
  const answerString = isMultipartQuestion(q) ? multipartAnswerToString(q.answer) : q.answer;

  return (
    <CardBody>
      <QCardItems
        assignees={q.assignees}
        stage={q.stage}
        owner={q.owner}
        id={q.id}
        stageType={q.stageType}
        readOnly={readOnly}
      />
      {(!isMultipartQuestion(q) || (isMultipartQuestion(q) && showMultiPartAnswer)) && (
        <Box>
          <Text fontWeight="bold" fontSize="body.s" mb={2}>
            Answer:
          </Text>
          <RichTextViewer content={answerString} />
        </Box>
      )}
      <AdditionalStatuses
        statuses={{
          decisionUsefulness: q?.decisionUsefulness,
          sensitivity: q?.sensitivity,
          confidence: q?.confidence,
          completeness: q?.completeness,
        }}
        questionIds={[q?.id]}
        questions={[q]}
        readOnly={readOnly}
      />
    </CardBody>
  );
}

export default QuestionCard;
